/**
 * Shared utility for consistent status colors across the application
 */

export type Status = 'active' | 'completed' | 'in_progress' | 'pending' | 'maintenance' | 'issues' | 'inactive';

export interface StatusColorConfig {
  bg: string;
  text: string;
  border: string;
  dot: string;
}

/**
 * Get Tailwind CSS classes for status colors
 */
export function getStatusColor(status: string): string {
  switch (status.toLowerCase()) {
    case 'completed':
    case 'active':
      return 'bg-status-success/10 text-status-success border-status-success/20';
    case 'in_progress':
    case 'maintenance':
      return 'bg-status-warning/10 text-status-warning border-status-warning/20';
    case 'pending':
    case 'issues':
      return 'bg-status-critical/10 text-status-critical border-status-critical/20';
    case 'inactive':
      return 'bg-muted text-muted-foreground border-border';
    default:
      return 'bg-status-info/10 text-status-info border-status-info/20';
  }
}

/**
 * Get human-readable status label
 */
export function getStatusLabel(status: string): string {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'Completed';
    case 'in_progress':
      return 'In Progress';
    case 'pending':
      return 'Pending';
    case 'active':
      return 'Active';
    case 'maintenance':
      return 'Maintenance';
    case 'issues':
      return 'Issues';
    case 'inactive':
      return 'Inactive';
    default:
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
}

/**
 * Pipeline (processing_status) lives in its own column, separate from lifecycle `status`.
 * Values come from the migration's CHECK constraint:
 *   PENDING_OCR, PENDING_AI, FAILED_OCR, FAILED_AI, COMPLETE, SKIPPED_NO_NAMEPLATE
 */
export type ProcessingStatus =
  | 'PENDING_OCR'
  | 'PENDING_AI'
  | 'FAILED_OCR'
  | 'FAILED_AI'
  | 'COMPLETE'
  | 'SKIPPED_NO_NAMEPLATE';

export function getProcessingStatusColor(status: string | null | undefined): string {
  switch (status) {
    case 'COMPLETE':
      return 'bg-status-success/10 text-status-success border-status-success/30';
    case 'PENDING_OCR':
    case 'PENDING_AI':
      return 'bg-status-info/10 text-status-info border-status-info/30';
    case 'FAILED_OCR':
    case 'FAILED_AI':
      return 'bg-status-critical/10 text-status-critical border-status-critical/30';
    case 'SKIPPED_NO_NAMEPLATE':
      return 'bg-muted text-muted-foreground border-border';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
}

export function getProcessingStatusLabel(status: string | null | undefined): string {
  switch (status) {
    case 'PENDING_OCR':       return 'Awaiting OCR';
    case 'PENDING_AI':        return 'Awaiting AI';
    case 'FAILED_OCR':        return 'OCR failed';
    case 'FAILED_AI':         return 'AI failed';
    case 'COMPLETE':          return 'Analyzed';
    case 'SKIPPED_NO_NAMEPLATE': return 'No nameplate';
    default:                  return status ?? 'Unprocessed';
  }
}

/**
 * Get detailed status color configuration
 */
export function getStatusColorConfig(status: string): StatusColorConfig {
  switch (status.toLowerCase()) {
    case 'completed':
    case 'active':
      return {
        bg: 'bg-status-success/10',
        text: 'text-status-success',
        border: 'border-status-success/20',
        dot: 'bg-status-success'
      };
    case 'in_progress':
    case 'maintenance':
      return {
        bg: 'bg-status-warning/10',
        text: 'text-status-warning',
        border: 'border-status-warning/20',
        dot: 'bg-status-warning'
      };
    case 'pending':
    case 'issues':
      return {
        bg: 'bg-status-critical/10',
        text: 'text-status-critical',
        border: 'border-status-critical/20',
        dot: 'bg-status-critical'
      };
    case 'inactive':
      return {
        bg: 'bg-muted',
        text: 'text-muted-foreground',
        border: 'border-border',
        dot: 'bg-muted-foreground'
      };
    default:
      return {
        bg: 'bg-status-info/10',
        text: 'text-status-info',
        border: 'border-status-info/20',
        dot: 'bg-status-info'
      };
  }
}
